import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Separator } from './components/ui/separator';
import { Alert, AlertDescription } from './components/ui/alert';
import { toast, Toaster } from 'sonner';
import { 
  Shield, 
  Wallet, 
  Mail, 
  User, 
  Link2, 
  CheckCircle, 
  XCircle, 
  Eye,
  EyeOff,
  Globe,
  Fingerprint,
  ArrowRight,
  BarChart3,
  Users,
  Zap,
  Star,
  UserCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', username: '' });
  const [profileForm, setProfileForm] = useState({ username: '', bio: '', location: '' });

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user?.id]); // Only fetch stats when user ID changes

  const checkAuthStatus = () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/identity/profile`);
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/identity/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('access_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      setLoginForm({ email: '', password: '' });
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('access_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      setRegisterForm({ email: '', password: '', username: '' });
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      toast.error('MetaMask is not installed. Please install MetaMask to connect your wallet.');
      return;
    }

    setLoading(true);
    try {
      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      
      if (accounts.length === 0) {
        toast.error('No accounts found. Please make sure MetaMask is unlocked.');
        return;
      }

      const walletAddress = accounts[0];
      
      // Get challenge from backend
      const challengeResponse = await axios.get(`${API}/auth/challenge/${walletAddress}`);
      const { challenge_id, challenge } = challengeResponse.data;
      
      // Request signature from MetaMask
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [challenge, walletAddress],
      });

      if (user) {
        // Link wallet to existing account
        await axios.post(`${API}/identity/link-wallet`, {
          wallet_address: walletAddress,
          signature,
          challenge_id
        });
        toast.success('Wallet linked successfully!');
        fetchUserProfile();
      } else {
        // Authenticate with wallet only
        const authResponse = await axios.post(`${API}/auth/wallet`, {
          wallet_address: walletAddress,
          signature,
          challenge_id
        });
        
        const { access_token, user: userData } = authResponse.data;
        localStorage.setItem('access_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setUser(userData);
        toast.success('Connected with wallet!');
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect wallet');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/identity/profile`, {
        username: profileForm.username,
        profile: {
          bio: profileForm.bio,
          location: profileForm.location
        }
      });
      toast.success('Profile updated successfully!');
      fetchUserProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setActiveTab('dashboard');
    toast.success('Logged out successfully');
  };

  const formatAddress = (address) => {
    return address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-lime-50 to-orange-50 relative overflow-hidden">
        {/* Floating Geometric Shapes */}
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
          <div className="shape shape-6"></div>
          <div className="shape shape-7"></div>
          <div className="shape shape-8"></div>
        </div>

        {/* Hero Section */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 py-16">
            <div className="text-center mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-4 font-display">
                Identity Bridge
              </h1>
              <p className="text-lg text-gray-700 mb-8 max-w-md mx-auto">
                Bridge your web2 and web3 identities
              </p>
              <h2 className="text-4xl font-bold text-gray-900 mb-12 max-w-4xl mx-auto leading-tight">
                Simplifying the digital world with secure and trusted identities
              </h2>
            </div>

            {/* Features Section with Gradient */}
            <div className="relative mb-16">
              <div className="gradient-section">
                <div className="gradient-content">
                  <div className="grid md:grid-cols-3 gap-8 text-center text-white">
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold">Secure Bridge</h3>
                      <p className="text-white/90">Advanced cryptographic security for cross-platform identity management</p>
                    </div>
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto">
                        <Link2 className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold">Unified Identity</h3>
                      <p className="text-white/90">Connect all your digital identities in one secure dashboard</p>
                    </div>
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto">
                        <Fingerprint className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold">Privacy First</h3>
                      <p className="text-white/90">Your data stays secure with decentralized verification</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Community Section */}
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Built for the community.</h2>
              <h3 className="text-3xl font-bold text-gray-900 mb-8">By the community.</h3>
              
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div className="flex items-center space-x-3 bg-white/60 backdrop-blur-sm rounded-2xl p-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" />
                    <AvatarFallback>JS</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">John Smith</p>
                    <p className="text-sm text-gray-600">Web3 Developer</p>
                  </div>
                  <Star className="w-5 h-5 text-orange-500 fill-current ml-auto" />
                </div>

                <div className="flex items-center space-x-3 bg-white/60 backdrop-blur-sm rounded-2xl p-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src="https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=100&h=100&fit=crop&crop=face" />
                    <AvatarFallback>AM</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Alice Miller</p>
                    <p className="text-sm text-gray-600">Security Expert</p>
                  </div>
                  <UserCheck className="w-5 h-5 text-green-500 ml-auto" />
                </div>

                <div className="flex items-center space-x-3 bg-white/60 backdrop-blur-sm rounded-2xl p-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" />
                    <AvatarFallback>MJ</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Mike Johnson</p>
                    <p className="text-sm text-gray-600">DeFi Pioneer</p>
                  </div>
                  <Shield className="w-5 h-5 text-blue-500 ml-auto" />
                </div>
              </div>
            </div>

            {/* Authentication Forms */}
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Enter One Digital Identity</h2>
              </div>

              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/60 backdrop-blur-sm">
                  <TabsTrigger value="login" className="text-lg py-3 data-[state=active]:bg-white">Sign In</TabsTrigger>
                  <TabsTrigger value="register" className="text-lg py-3 data-[state=active]:bg-white">Create Account</TabsTrigger>
                </TabsList>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Traditional Auth */}
                  <div>
                    <TabsContent value="login">
                      <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-lg">
                        <CardHeader className="text-center pb-6">
                          <div className="mx-auto w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-4">
                            <Mail className="w-6 h-6 text-white" />
                          </div>
                          <CardTitle className="text-2xl">Web2 Sign In</CardTitle>
                          <CardDescription>Use your email and password</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="email">Email</Label>
                              <Input
                                id="email"
                                type="email"
                                value={loginForm.email}
                                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                                placeholder="Enter your email"
                                required
                                className="h-12 border-0 bg-gray-50/80"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="password">Password</Label>
                              <div className="relative">
                                <Input
                                  id="password"
                                  type={showPassword ? "text" : "password"}
                                  value={loginForm.password}
                                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                                  placeholder="Enter your password"
                                  required
                                  className="h-12 pr-10 border-0 bg-gray-50/80"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                            <Button 
                              type="submit" 
                              className="w-full h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium border-0"
                              disabled={loading}
                            >
                              {loading ? 'Signing in...' : 'Sign In'}
                            </Button>
                          </form>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="register">
                      <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-lg">
                        <CardHeader className="text-center pb-6">
                          <div className="mx-auto w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-600 rounded-full flex items-center justify-center mb-4">
                            <User className="w-6 h-6 text-white" />
                          </div>
                          <CardTitle className="text-2xl">Create Account</CardTitle>
                          <CardDescription>Start your identity bridge journey</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={handleRegister} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="reg-email">Email</Label>
                              <Input
                                id="reg-email"
                                type="email"
                                value={registerForm.email}
                                onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                                placeholder="Enter your email"
                                required
                                className="h-12 border-0 bg-gray-50/80"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="username">Username (Optional)</Label>
                              <Input
                                id="username"
                                type="text"
                                value={registerForm.username}
                                onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                                placeholder="Choose a username"
                                className="h-12 border-0 bg-gray-50/80"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="reg-password">Password</Label>
                              <div className="relative">
                                <Input
                                  id="reg-password"
                                  type={showPassword ? "text" : "password"}
                                  value={registerForm.password}
                                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                                  placeholder="Create a strong password"
                                  required
                                  className="h-12 pr-10 border-0 bg-gray-50/80"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                            <Button 
                              type="submit" 
                              className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-medium border-0"
                              disabled={loading}
                            >
                              {loading ? 'Creating Account...' : 'Create Account'}
                            </Button>
                          </form>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </div>

                  {/* Web3 Auth */}
                  <div className="flex flex-col justify-center">
                    <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-lg">
                      <CardHeader className="text-center pb-6">
                        <div className="mx-auto w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center mb-4">
                          <Wallet className="w-6 h-6 text-white" />
                        </div>
                        <CardTitle className="text-2xl">Web3 Connect</CardTitle>
                        <CardDescription>Connect with your crypto wallet</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="text-center">
                          <div className="w-full h-32 bg-gradient-to-r from-emerald-400 to-orange-500 rounded-lg mb-4 flex items-center justify-center">
                            <div className="text-white text-lg font-semibold">Web3 Gateway</div>
                          </div>
                        </div>
                        <Button 
                          onClick={connectWallet}
                          className="w-full h-12 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-medium border-0"
                          disabled={loading}
                        >
                          {loading ? 'Connecting...' : (
                            <>
                              <Wallet className="w-5 h-5 mr-2" />
                              Connect Wallet
                            </>
                          )}
                        </Button>
                        <div className="text-center text-sm text-gray-500">
                          <p>Supports MetaMask and other Ethereum wallets</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-lime-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-green-600 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 font-display">Identity Bridge</h1>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-emerald-100 text-emerald-700">
                    {user.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-gray-700">
                  {user.username || user.email || formatAddress(user.wallet_address)}
                </span>
              </div>
              <Button onClick={logout} variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 bg-white/60 backdrop-blur-sm">
            <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="identities" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Link2 className="w-4 h-4" />
              Identities
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2 data-[state=active]:bg-white">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 font-display">Welcome to Your Identity Bridge</h2>
              <p className="text-gray-600">Manage and secure your digital identities across web2 and web3</p>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
                      </div>
                      <Users className="w-8 h-8 text-emerald-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Web2 Only</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.web2_only}</p>
                      </div>
                      <Mail className="w-8 h-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Web3 Only</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.web3_only}</p>
                      </div>
                      <Wallet className="w-8 h-8 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Bridged</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.bridged_identities}</p>
                      </div>
                      <Link2 className="w-8 h-8 text-amber-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Quick Actions */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {!user.wallet_address && (
                    <Button 
                      onClick={connectWallet}
                      className="h-16 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white border-0"
                      disabled={loading}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Wallet className="w-5 h-5" />
                        <span>Connect Wallet</span>
                      </div>
                    </Button>
                  )}
                  <Button 
                    onClick={() => setActiveTab('profile')}
                    variant="outline"
                    className="h-16 border-emerald-200 hover:bg-emerald-50 text-emerald-700"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <User className="w-5 h-5" />
                      <span>Update Profile</span>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Identities Tab */}
          <TabsContent value="identities" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 font-display">Connected Identities</h2>
              <p className="text-gray-600">Manage your web2 and web3 identity connections</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Web2 Identity */}
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-emerald-600" />
                    Web2 Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-emerald-50/80 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="text-sm text-gray-600">{user.email || 'Not connected'}</p>
                      </div>
                    </div>
                    {user.email ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-emerald-50/80 rounded-lg">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium">Username</p>
                        <p className="text-sm text-gray-600">{user.username || 'Not set'}</p>
                      </div>
                    </div>
                    {user.username ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Badge variant="secondary" className="bg-gray-100">Optional</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Web3 Identity */}
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-orange-600" />
                    Web3 Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-orange-50/80 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium">Wallet Address</p>
                        <p className="text-sm text-gray-600 font-mono">
                          {user.wallet_address ? formatAddress(user.wallet_address) : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    {user.wallet_address ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  {!user.wallet_address && (
                    <Button 
                      onClick={connectWallet}
                      className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white border-0"
                      disabled={loading}
                    >
                      <Wallet className="w-4 h-4 mr-2" />
                      {loading ? 'Connecting...' : 'Connect Wallet'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Bridge Status */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-amber-600" />
                  Identity Bridge Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user.email && user.wallet_address ? (
                  <Alert className="border-green-200 bg-green-50/80">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      🎉 Your identities are successfully bridged! You can now seamlessly authenticate across web2 and web3 platforms.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="border-amber-200 bg-amber-50/80">
                    <ArrowRight className="w-4 h-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      Connect both web2 and web3 identities to unlock the full power of cross-platform authentication.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 font-display">Profile Settings</h2>
              <p className="text-gray-600">Update your profile information and preferences</p>
            </div>

            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal information and preferences</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={updateProfile} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile-username">Username</Label>
                    <Input
                      id="profile-username"
                      type="text"
                      value={profileForm.username}
                      onChange={(e) => setProfileForm({...profileForm, username: e.target.value})}
                      placeholder={user.username || "Enter your username"}
                      className="h-12 border-0 bg-gray-50/80"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Input
                      id="bio"
                      type="text"
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                      placeholder="Tell us about yourself"
                      className="h-12 border-0 bg-gray-50/80"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      type="text"
                      value={profileForm.location}
                      onChange={(e) => setProfileForm({...profileForm, location: e.target.value})}
                      placeholder="Where are you based?"
                      className="h-12 border-0 bg-gray-50/80"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white border-0"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;